import { type PropsWithChildren } from 'react';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import { ImageBackground } from 'expo-image';
import { getContentfulImageUrl, theme } from '@sg/garnish';

import { useProductDetailsNavigationHeaderHeight } from '../../../ProductDetailsNavigationHeader';

// ─────────────────────────────────────────────────────────────────────────────

const ProductDetailsHeaderSmallOuterContainer = (
  props: OuterContainerProps,
) => {
  const {
    children,
    backgroundColor = theme.colors.KALE,
    backgroundImageBaseUrl,
  } = props;

  const navHeaderHeight = useProductDetailsNavigationHeaderHeight();

  // ─── Derived Data ────────────────────────────────────────────────────

  const imageSource = backgroundImageBaseUrl
    ? getContentfulImageUrl(backgroundImageBaseUrl, { w: 400, fit: 'fill', f: 'top' }) // prettier-ignore
    : undefined;

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStyles = useStyle(
    () => ({ backgroundColor, paddingTop: navHeaderHeight }),
    [backgroundColor, navHeaderHeight],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={[styles.outerContainer, containerDynamicStyles]}>
      {imageSource ? (
        <ImageBackground source={imageSource} style={StyleSheet.absoluteFill} />
      ) : null}

      {children}
    </View>
  );
};

const ProductDetailsHeaderSmallContainer = (props: Props) => {
  const { children } = props;

  return <View style={styles.container}>{children}</View>;
};

const ProductDetailsHeaderSmallContentContainer = (props: Props) => {
  const { children } = props;

  return <View style={styles.contentContainer}>{children}</View>;
};

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsHeaderSmall = {
  OuterContainer: ProductDetailsHeaderSmallOuterContainer,
  Container: ProductDetailsHeaderSmallContainer,
  ContentContainer: ProductDetailsHeaderSmallContentContainer,
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    gap: theme.spacing['6'],
    paddingHorizontal: theme.spacing['6'],
    paddingBottom: theme.spacing['6'],
  },
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['1'],
  },
  contentContainer: {
    flex: 1,
    paddingTop: theme.spacing['5'],
    gap: theme.spacing['6'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren;

type OuterContainerProps = PropsWithChildren<{
  backgroundColor: string | undefined;
  backgroundImageBaseUrl: string | undefined;
}>;
